import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AdminContextProvider, useAdminContext } from '../contexts/AdminContext';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const ADMIN_NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', exact: true },
  { path: '/admin/portrait', label: 'Portrait' },
  { path: '/admin/faq', label: 'FAQ' },
  { path: '/admin/glossary', label: 'Glossar' },
  { path: '/admin/blog', label: 'Blog' },
  { path: '/admin/impressum', label: 'Impressum' },
  { path: '/admin/media', label: 'Media' },
  { path: '/admin/inquiries', label: 'Kontaktanfragen', badge: true },
  { path: '/admin/bookings', label: 'Termine', bookingBadge: true },
  { path: '/admin/booking-settings', label: 'Terminbuchung' },
  { path: '/admin/email-templates', label: 'E-Mail Vorlagen' },
  { path: '/admin/settings', label: 'Einstellungen' },
];

const AdminLayoutInner: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const { newInquiriesCount, updatedInquiriesCount, pendingBookingsCount } = useAdminContext();
  const userName = accounts[0]?.name || accounts[0]?.username || 'Admin';
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-primary-950 text-white flex flex-col transition-transform duration-300 md:static md:translate-x-0 md:shrink-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 border-b border-primary-800 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">CLAIRMONT Admin</h1>
            <p className="text-xs text-gray-400 mt-1 truncate">{userName}</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-white text-xl leading-none"
            aria-label="Sidebar schließen"
          >
            ✕
          </button>
        </div>
        <nav className="flex-1 py-4">
          {ADMIN_NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center justify-between px-4 py-2.5 text-sm transition ${
                isActive(item.path, item.exact)
                  ? 'bg-primary-700 text-white font-medium'
                  : 'text-gray-300 hover:bg-primary-800 hover:text-white'
              }`}
            >
              <span>{item.label}</span>
              {item.badge && (
                <span className="flex gap-1">
                  {newInquiriesCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">
                      {newInquiriesCount}
                    </span>
                  )}
                  {updatedInquiriesCount > 0 && (
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">
                      {updatedInquiriesCount}
                    </span>
                  )}
                </span>
              )}
              {item.bookingBadge && pendingBookingsCount > 0 && (
                <span className="bg-yellow-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">
                  {pendingBookingsCount}
                </span>
              )}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-primary-800">
          <Link to="/de" className="block text-xs text-gray-400 hover:text-white mb-2 transition">
            &larr; Zur Website
          </Link>
          <button
            onClick={() => instance.logoutRedirect()}
            className="text-xs text-red-400 hover:text-red-300 transition"
          >
            Abmelden
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 bg-primary-950 text-white px-4 py-3 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white text-xl leading-none"
            aria-label="Menü öffnen"
          >
            ☰
          </button>
          <span className="font-semibold text-sm">CLAIRMONT Admin</span>
        </div>
        <div className="p-4 sm:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => (
  <AdminContextProvider>
    <AdminLayoutInner>{children}</AdminLayoutInner>
  </AdminContextProvider>
);

export default AdminLayout;
